import { Filter as FilterIcon } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { filtersFromParams } from 'features/agent-analytics/utils/deepLinkFilters';
import axiosInstance from 'shared/services/api/axiosInstance';
import { dedupedGet } from 'shared/services/api/dedupedGet';
import TableSkeleton from 'shared/components/ui/TableSkeleton';
import SpansTable from 'features/agent-analytics/components/SpansTable';
import SpansColumnsButton from 'features/agent-analytics/components/SpansColumnsButton';
import RunsHistogram from 'features/agent-analytics/components/RunsHistogram';
import TracesViewTabs from 'features/agent-analytics/components/TracesViewTabs';
import SpansQueryBuilder, {
  buildSpanModularFilters,
} from 'features/agent-analytics/components/SpansQueryBuilder';
import SemanticSearchBar from 'features/agent-analytics/components/SemanticSearchBar';
import TraceDetailPage from 'features/agent-analytics/pages/TraceDetailPage';
import AgentRunDetailPage from 'features/agent-analytics/pages/AgentRunDetailPage';
import { useBreadcrumb } from 'shared/context/BreadcrumbContext';
import { trackClientEvent } from 'shared/utils/trodoEvents';
import EventsTimeFilter from 'features/activity/components/EventsTimeFilter';
import {
  DEFAULT_SPAN_COLUMN_KEYS, loadSpanColumnKeys, saveSpanColumnKeys, resolveSpanColumns,
} from 'features/agent-analytics/utils/spanColumns';
import {
  activityToolbarButtonClass,
} from 'features/activity/styles/dataTableStyles';
import TableFooter from 'shared/components/tables/TableFooter';
import { presetToISORange, DEFAULT_LIST_PRESET } from 'shared/utils/timePresets';
import { useAnnotations } from 'features/agent-analytics/annotations';
import BulkBar from 'shared/components/tables/BulkBar';
import AddToDatasetButton from 'features/datasets/components/AddToDatasetButton';
import SourceDialog from 'features/datasets/components/SourceDialog';
import { withEvalFilter, evalConditionCount, readEvalLines, writeEvalLines } from 'features/agent-analytics/components/EvalFilterSection';

const PAGE_SIZE = 25;

function useSelectedTeam() {
  const [team, setTeam] = useState(() =>
    typeof window === 'undefined' ? '' : localStorage.getItem('selectedTeam') || ''
  );
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'selectedTeam') setTeam(e.newValue || '');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  return team;
}

/** Decode a JSON-encoded URL search param expecting an array. */
function readJsonArrayParam(searchParams, key) {
  const raw = searchParams.get(key);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(decodeURIComponent(raw));
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Trace page — paginated list of individual spans (the span analog of the
 * Agent Runs page). Includes runless spans (those written via track_mcp /
 * trackMcp) so MCP traffic is fully visible here even though it has no
 * parent agent_runs row.
 */
export default function TracesPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * The run, span or conversation the link that opened this page pointed at.
   *
   * The effect below rebuilds the query string from this page's own state, so
   * the first render after arriving from a notification threw these away —
   * the drawer had already captured the id, but the URL no longer said so,
   * and a reload or a shared copy of it opened nothing.
   *
   * Captured ONCE, on mount. Reading them from `searchParams` inside the
   * effect would mean depending on the thing the effect writes, which is an
   * infinite loop — and these are arrival values that never change anyway.
   */
  const [deepLinkParams] = useState(() => {
    const out = {};
    for (const k of ['run_id', 'span_id', 'span_started_at', 'conversation_id', 'tab']) {
      const v = searchParams.get(k);
      if (v) out[k] = v;
    }
    return out;
  });
  const teamId = useSelectedTeam();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Visual query builder state — three lists, mirroring the Runs page.
  const [selectedTraces, setSelectedTraces] = useState(
    () => readJsonArrayParam(searchParams, 'traces'),
  );
  const [traceScopedFilters, setTraceScopedFilters] = useState(
    () => readJsonArrayParam(searchParams, 'traceFilters'),
  );
  // Chips from the query builder, plus any the deep link brought with it —
  // "Open in Traces" under a chat answer arrives with the answer's own
  // filters as plain params, and they belong in the same list.
  const [globalFilters, setGlobalFilters] = useState(() => {
    const fromJson = readJsonArrayParam(searchParams, 'globalFilters');
    if (fromJson.length) return fromJson;
    return filtersFromParams(searchParams, 'spans');
  });

  // EVALS section of the filter: what the evals said about the span or its run (URL `evals`).
  const [evalLines, setEvalLines] = useState(() => readEvalLines(searchParams));

  const [periodTotals, setPeriodTotals] = useState({ total: 0, errors: 0 });
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  // { candidates, kept } when the server dropped search hits that fell outside
  // the active filters — so a short result list can say why it is short.
  const [searchNarrowed, setSearchNarrowed] = useState(null);
  const [selectedSpan, setSelectedSpan] = useState(null);
  const [deepSpanId] = useState(() => searchParams.get('span_id') || '');
  const [deepSpanStartedAt] = useState(() => searchParams.get('span_started_at') || '');
  const [deepSpanTab] = useState(() => searchParams.get('tab') || '');
  const [spanDrawerTab, setSpanDrawerTab] = useState(() => (
    searchParams.get('tab') === 'annotations' ? 'annotations' : null
  ));
  const { prefetchCounts } = useAnnotations();
  // Run drawer overlay: when a row's run pill or the span-detail drawer's
  // run link is clicked, we open the existing AgentRunDetailPage on top of
  // the trace surface so the user never leaves the page.
  const [selectedRunId, setSelectedRunId] = useState(null);

  // Memoize modular filters by content so child components (chart, totals
  // chip) don't refetch on every unrelated parent render.
  const stableSpanFilters = useMemo(
    () => withEvalFilter(buildSpanModularFilters({ selectedTraces, traceScopedFilters, globalFilters }), evalLines),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(selectedTraces), JSON.stringify(traceScopedFilters), JSON.stringify(globalFilters), JSON.stringify(evalLines)]
  );

  // Breadcrumb reflects open drawers so the global top bar always shows the path.
  useBreadcrumb(
    selectedSpan
      ? [
          { label: 'Traces', to: `/${teamId}/agent-analytics/traces` },
          { label: selectedSpan.span_id || 'Span', copyable: true },
        ]
      : selectedRunId
      ? [
          { label: 'Traces', to: `/${teamId}/agent-analytics/traces` },
          { label: selectedRunId, copyable: true },
        ]
      : [{ label: 'Traces' }]
  );

  const [filterShown, setFilterShown] = useState(
    () =>
      readJsonArrayParam(searchParams, 'traces').length +
        readJsonArrayParam(searchParams, 'traceFilters').length +
        readJsonArrayParam(searchParams, 'globalFilters').length +
        readEvalLines(searchParams).length >
      0,
  );

  // Default 30d so the API gets a real window on first paint (empty preset
  // → empty range previously meant "return everything").
  const [timePreset, setTimePreset] = useState(searchParams.get('preset') || DEFAULT_LIST_PRESET);
  const [timeRange, setTimeRange] = useState(() => {
    const urlFrom = searchParams.get('time_start');
    const urlTo   = searchParams.get('time_end');
    if (urlFrom || urlTo) return { from: urlFrom || '', to: urlTo || '' };
    const seedPreset = searchParams.get('preset') || DEFAULT_LIST_PRESET;
    const seeded = presetToISORange(seedPreset);
    return seeded || { from: '', to: '' };
  });
  const onTimeFilterChange = (next) => {
    const preset = next?.preset || (next?.type === 'custom' ? 'custom' : '');
    setTimePreset(preset);
    if (next?.from || next?.to) {
      setTimeRange({ from: next?.from || '', to: next?.to || '' });
    } else {
      const seeded = presetToISORange(preset);
      setTimeRange(seeded || { from: '', to: '' });
    }
  };

  // Column preferences — persisted in localStorage per team (lighter than
  // the runs page's server-side preferences, which were overkill for a
  // page that's accessed less often).
  const [columnKeys, setColumnKeys] = useState(() => loadSpanColumnKeys(teamId));
  useEffect(() => { setColumnKeys(loadSpanColumnKeys(teamId)); }, [teamId]);
  const onColumnsChange = (next) => {
    const valid = next.length > 0 ? next : [...DEFAULT_SPAN_COLUMN_KEYS];
    setColumnKeys(valid);
    saveSpanColumnKeys(teamId, valid);
  };

  const requestIdRef = useRef(0);

  const syncUrl = useCallback(() => {
    const next = new URLSearchParams();
    // Carry the deep-link keys through — see deepLinkParams.
    for (const [k, v] of Object.entries(deepLinkParams)) next.set(k, v);
    if (selectedTraces.length > 0) {
      next.set('traces', encodeURIComponent(JSON.stringify(selectedTraces)));
    }
    if (traceScopedFilters.length > 0) {
      next.set('traceFilters', encodeURIComponent(JSON.stringify(traceScopedFilters)));
    }
    if (globalFilters.length > 0) {
      next.set('globalFilters', encodeURIComponent(JSON.stringify(globalFilters)));
    }
    writeEvalLines(next, evalLines);
    if (searchQuery) next.set('q', searchQuery);
    if (timeRange.from) next.set('time_start', timeRange.from);
    if (timeRange.to)   next.set('time_end',   timeRange.to);
    if (timePreset)     next.set('preset', timePreset);
    setSearchParams(next, { replace: true });
  }, [
    selectedTraces, traceScopedFilters, globalFilters, evalLines,
    searchQuery, timeRange, timePreset, setSearchParams, deepLinkParams,
  ]);

  const loadSpans = useCallback(
    async (appendOffset = 0) => {
      if (!teamId) {
        setError('Select a team to view spans.');
        setRows([]);
        setLoading(false);
        return;
      }
      const id = ++requestIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const modularFilters = withEvalFilter(buildSpanModularFilters({
          selectedTraces, traceScopedFilters, globalFilters,
        }), evalLines);
        if (searchQuery) {
          // Search NARROWS the filters — it does not replace them. This call
          // used to send only team_id and q, so typing in the search box
          // silently dropped every filter chip and the time range while the
          // chips stayed on screen claiming to be active.
          const params = { team_id: teamId, q: searchQuery, limit: 100 };
          if (modularFilters.length > 0) {
            params.modularFilters = encodeURIComponent(JSON.stringify(modularFilters));
          }
          if (timeRange.from) params.time_start = timeRange.from;
          if (timeRange.to)   params.time_end   = timeRange.to;
          const { data } = await axiosInstance.get('/agent-spans/search', { params });
          if (id !== requestIdRef.current) return;
          setRows(data?.spans || []);
          setTotal((data?.spans || []).length);
          setSearchNarrowed(data?.filtered || null);
          setHasMore(false);
          setOffset(0);
        } else {
          const params = {
            team_id: teamId,
            limit: PAGE_SIZE,
            offset: appendOffset,
          };
          if (modularFilters.length > 0) {
            params.modularFilters = encodeURIComponent(JSON.stringify(modularFilters));
          }
          if (timeRange.from) params.time_start = timeRange.from;
          if (timeRange.to)   params.time_end   = timeRange.to;
          const { data } = await dedupedGet('/agent-spans', { params });
          if (id !== requestIdRef.current) return;
          setSearchNarrowed(null);
          const incoming = data?.spans || [];
          const totalCount = data?.pagination?.total ?? incoming.length;
          setRows((prev) => (appendOffset === 0 ? incoming : [...prev, ...incoming]));
          setTotal(totalCount);
          setHasMore(Boolean(data?.pagination?.hasMore));
          setOffset(appendOffset + incoming.length);
        }
      } catch (err) {
        if (id !== requestIdRef.current) return;
        setRows([]);
        setTotal(0);
        setHasMore(false);
        setOffset(0);
        setError(err?.response?.data?.error || err?.message || 'Failed to load spans.');
      } finally {
        if (id === requestIdRef.current) setLoading(false);
      }
    },
    [teamId, selectedTraces, traceScopedFilters, globalFilters, evalLines, searchQuery, timeRange]
  );

  useEffect(() => {
    syncUrl();
    loadSpans(0);
  }, [loadSpans, syncUrl]);

  const closeDrawer = () => setSelectedSpan(null);
  const openRunDrawer = useCallback((runId) => setSelectedRunId(runId), []);
  const closeRunDrawer = useCallback(() => setSelectedRunId(null), []);

  useEffect(() => {
    if (!deepSpanId) return;
    if (deepSpanTab === 'annotations') setSpanDrawerTab('activity');
    setSelectedSpan((prev) => (
      prev?.span_id === deepSpanId
        ? prev
        : { span_id: deepSpanId, started_at: deepSpanStartedAt || undefined }
    ));
  }, [deepSpanId, deepSpanStartedAt, deepSpanTab]);
  useEffect(() => {
    prefetchCounts({
      spans: rows.map((r) => ({ span_id: r.span_id, span_started_at: r.started_at })).filter((s) => s.span_id),
    });
  }, [rows, prefetchCounts]);
  // Clicking a parent_span_id swaps the active span in the trace drawer
  // — same drawer, new span. Mirrors how AgentRunDetailPage lets you
  // jump between sibling runs without re-opening the drawer.
  const openSpanDrawer = useCallback(
    (spanId) => setSelectedSpan({ span_id: spanId }),
    [],
  );

  const activeFilterCount =
    selectedTraces.length + traceScopedFilters.length + globalFilters.length + evalConditionCount(evalLines);

  // Ticked spans → "Add to dataset…"; the page's filter → "Create dataset from this filter".
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [datasetDialog, setDatasetDialog] = useState(false);
  useEffect(() => { setCheckedIds(new Set()); }, [selectedTraces, traceScopedFilters, globalFilters, evalLines, timeRange, searchQuery]);
  const toggleChecked = useCallback((id) => setCheckedIds((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; }), []);
  const toggleAllChecked = useCallback((on) => setCheckedIds(on ? new Set(rows.map((r) => r.span_id)) : new Set()), [rows]);
  const datasetInitial = {
    kind: 'spans', selectedTraces, traceScopedFilters, globalFilters, evalConditions: evalLines,
    time: { preset: timePreset, from: timeRange.from, to: timeRange.to },
  };

  return (
    <main
      className="flex flex-col bg-surface"
      style={{ fontFamily: 'Inter, sans-serif' }}
    >
      {/* Injects the Conversations/Runs/Spans switcher into the top bar. */}
      <TracesViewTabs value="spans" />
      <div className="flex flex-col gap-2 bg-surface px-3 sm:px-4 pb-2 pt-3">
        {/* Toolbar: time-period · totals · search · Add filter ··· Edit columns */}
        <div className="flex flex-wrap items-center gap-2">
          <EventsTimeFilter
            selectedPreset={timePreset || DEFAULT_LIST_PRESET}
            onTimeFilterChange={onTimeFilterChange}
          />
          {/* Period totals for the selected window, beside the dates */}
          {!searchQuery && (
            <div className="inline-flex h-8 items-stretch overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
              <div className="flex items-center gap-1.5 px-3 text-[12px]">
                <span className="font-semibold tabular-nums text-[color:var(--text-primary)]">
                  {periodTotals.total.toLocaleString()}
                </span>
                <span className="text-[color:var(--text-muted)]">Total</span>
              </div>
              <div className="flex items-center gap-1.5 border-l border-[color:var(--border-default)] px-3 text-[12px]">
                <span className="inline-block h-2 w-2 rounded-[2px] bg-[color:var(--feedback-error)]" aria-hidden />
                <span className="font-semibold tabular-nums text-[color:var(--text-primary)]">
                  {periodTotals.errors.toLocaleString()}
                </span>
                <span className="text-[color:var(--text-muted)]">Error</span>
              </div>
            </div>
          )}
          <SemanticSearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search spans or scores (ok=false)…"
            width={240}
          />
          <button
            type="button"
            onClick={() => setFilterShown((v) => !v)}
            className={activityToolbarButtonClass}
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            <FilterIcon size={14} color="var(--text-muted)" />
            Filter
            {activeFilterCount > 0 ? (
              <span className="rounded-full bg-surface-hover px-1.5 text-[10px] font-semibold text-[color:var(--text-primary)]">
                {activeFilterCount}
              </span>
            ) : null}
          </button>
          <button type="button" onClick={() => setDatasetDialog(true)} className={activityToolbarButtonClass}>
            Dataset from filter
          </button>
          <div className="ml-auto">
            <SpansColumnsButton columnKeys={columnKeys} onChange={onColumnsChange} />
          </div>
        </div>

        <SpansQueryBuilder
          teamId={teamId}
          selectedTraces={selectedTraces}
          onSelectedTracesChange={setSelectedTraces}
          traceScopedFilters={traceScopedFilters}
          onTraceScopedFiltersChange={setTraceScopedFilters}
          globalFilters={globalFilters}
          onGlobalFiltersChange={setGlobalFilters}
          evalLines={evalLines}
          onEvalLinesChange={setEvalLines}
          shown={filterShown}
        />

      </div>

      <div className="flex shrink-0 flex-col gap-3 px-4 pb-8 pt-3">
        {/* Timeline histogram: same filters as the table */}
        {!searchQuery && (
          <RunsHistogram
            endpoint="/agent-spans/histogram"
            noun="spans"
            onTotals={setPeriodTotals}
            teamId={teamId}
            timeRange={timeRange}
            modularFilters={stableSpanFilters}
          />
        )}

        {error ? (
          <div className="rounded-lg border border-[color:var(--feedback-error-bg)] bg-[color:var(--feedback-error-bg)] p-3 text-[12px] text-[color:var(--feedback-error)]">
            {error}
          </div>
        ) : null}

        {loading && rows.length === 0 ? (
          // Same resolver the table uses, so header and skeleton agree.
          <TableSkeleton
            columns={resolveSpanColumns(columnKeys).map((c) => c.label)}
            rows={12}
          />
        ) : (
          <SpansTable
            rows={rows}
            columnKeys={columnKeys}
            checkedIds={checkedIds}
            onToggleChecked={toggleChecked}
            onToggleAllChecked={toggleAllChecked}
            selectedSpanId={selectedSpan?.span_id || null}
            onRowClick={(span) => {
              trackClientEvent(
                'trace_span_opened',
                { span_id: span?.span_id ?? null, span_name: span?.name ?? null },
                { category: 'product' }
              );
              setSpanDrawerTab(null);
              setSelectedSpan(span);
            }}
            onAnnotationClick={(span) => {
              setSpanDrawerTab('activity');
              setSelectedSpan(span);
            }}
            onRunClick={openRunDrawer}
            onSpanClick={openSpanDrawer}
          />
        )}

        {/* Footer: count on the left, Load more centered */}
        {rows.length > 0 || total > 0 ? (
          <TableFooter
            shown={rows.length}
            total={total}
            hasMore={hasMore && !searchQuery}
            loading={loading}
            onLoadMore={() => loadSpans(offset)}
            note={(() => {
              if (loading && rows.length === 0) return 'Loading…';
              if (total === 0) return 'No spans match';
              // While searching, say what the search did RELATIVE to the
              // filters — a short list is otherwise indistinguishable from
              // "the search found little".
              if (searchQuery && searchNarrowed) {
                const { candidates, kept } = searchNarrowed;
                return kept < candidates
                  ? `${kept.toLocaleString()} of ${candidates.toLocaleString()} matches are inside your filters`
                  : `${kept.toLocaleString()} ${kept === 1 ? 'match' : 'matches'} inside your filters`;
              }
              if (searchQuery) {
                return `${rows.length.toLocaleString()} ${rows.length === 1 ? 'match' : 'matches'}`;
              }
              return null;
            })()}
          />
        ) : null}
      </div>

      <BulkBar count={checkedIds.size} label="Selected spans" onClear={() => setCheckedIds(new Set())}
        actions={[{ label: 'Create dataset from this filter', onClick: () => setDatasetDialog(true) }]}>
        <AddToDatasetButton selection={{ span_ids: [...checkedIds] }} onDone={() => setCheckedIds(new Set())} />
      </BulkBar>
      {datasetDialog ? <SourceDialog newDataset initial={datasetInitial} onClose={() => setDatasetDialog(false)} /> : null}

      {selectedSpan ? (() => {
        const idx = rows.findIndex((r) => r.span_id === selectedSpan.span_id);
        const gotoSpanAt = (next) => {
          if (next >= 0 && next < rows.length) setSelectedSpan(rows[next]);
        };
        return (
          <TraceDetailPage
            spanId={selectedSpan.span_id}
            teamId={teamId}
            initialTab={spanDrawerTab || undefined}
            onClose={closeDrawer}
            onRunClick={openRunDrawer}
            onSpanClick={openSpanDrawer}
            onPrev={() => gotoSpanAt(idx - 1)}
            onNext={() => gotoSpanAt(idx + 1)}
            hasPrev={idx > 0}
            hasNext={idx >= 0 && idx < rows.length - 1}
          />
        );
      })() : null}

      {/* Run-detail overlay: z-[70] in AgentRunDetailPage stacks above trace drawer (z-[60]). */}
      {selectedRunId ? (
        <AgentRunDetailPage
          runId={selectedRunId}
          teamId={teamId}
          onClose={closeRunDrawer}
          onChange={() => loadSpans(0)}
        />
      ) : null}
    </main>
  );
}
